import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { STATUS } from "@/constants";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type IntegrityIssue = {
  target_type: string;
  target_key: string;
  detail: string;
};

export type IntegrityReport = {
  issues: IntegrityIssue[];
  ok: boolean;
};

export type ReconciliationCheck = {
  id: string;
  metric_key: string;
  name: string;
  primary_sql: string;
  independent_sql: string;
  tolerance: number;
  last_run_at: string | null;
  last_primary_value: number | null;
  last_independent_value: number | null;
  last_within_tolerance: boolean | null;
  last_error_message: string | null;
  created_at: string | null;
};

export type DataQualityState = {
  domainId: number | null;
  integrityReport: IntegrityReport | null;
  integrityCheckStatus: "idle" | "loading" | "succeeded" | "failed";
  integrityCheckError: string | null;
  reconciliationChecks: ReconciliationCheck[];
  reconciliationChecksStatus: "idle" | "loading" | "succeeded" | "failed";
  reconciliationChecksError: string | null;
  createReconciliationCheckStatus: "idle" | "loading" | "succeeded" | "failed";
  createReconciliationCheckError: string | null;
  runningReconciliationCheckId: string | null;
  runReconciliationCheckError: string | null;
};

export const dataQualityInitialState: DataQualityState = {
  domainId: null,
  integrityReport: null,
  integrityCheckStatus: STATUS.IDLE,
  integrityCheckError: null,
  reconciliationChecks: [],
  reconciliationChecksStatus: STATUS.IDLE,
  reconciliationChecksError: null,
  createReconciliationCheckStatus: STATUS.IDLE,
  createReconciliationCheckError: null,
  runningReconciliationCheckId: null,
  runReconciliationCheckError: null,
};

function isIntegrityIssue(value: unknown): value is IntegrityIssue {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.target_type === "string" &&
    typeof row.target_key === "string" &&
    typeof row.detail === "string"
  );
}

function isIntegrityReport(value: unknown): value is IntegrityReport {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    Array.isArray(row.issues) &&
    row.issues.every(isIntegrityIssue) &&
    typeof row.ok === "boolean"
  );
}

function isReconciliationCheck(value: unknown): value is ReconciliationCheck {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "string" &&
    typeof row.metric_key === "string" &&
    typeof row.name === "string" &&
    typeof row.primary_sql === "string" &&
    typeof row.independent_sql === "string" &&
    typeof row.tolerance === "number"
  );
}

export const fetchDomainIntegrityCheck = createAsyncThunk<
  { domainId: number; report: IntegrityReport },
  number,
  { rejectValue: string }
>("dataQuality/fetchIntegrityCheck", async (domainId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/domains/${domainId}/semantic-model/integrity-check`);
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isIntegrityReport(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return { domainId, report: data };
  } catch (e) {
    const message = e instanceof Error ? e.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const fetchDomainReconciliationChecks = createAsyncThunk<
  { domainId: number; checks: ReconciliationCheck[] },
  number,
  { rejectValue: string }
>("dataQuality/fetchReconciliationChecks", async (domainId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/domains/${domainId}/semantic-model/reconciliation-checks`);
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!Array.isArray(data) || !data.every(isReconciliationCheck)) {
      return rejectWithValue("Unexpected response shape");
    }
    return { domainId, checks: data };
  } catch (e) {
    const message = e instanceof Error ? e.message : "Request failed";
    return rejectWithValue(message);
  }
});

export type CreateReconciliationCheckArgs = {
  domainId: number;
  metricKey: string;
  name: string;
  primarySql: string;
  independentSql: string;
  tolerance: number;
};

export const createDomainReconciliationCheck = createAsyncThunk<
  ReconciliationCheck,
  CreateReconciliationCheckArgs,
  { rejectValue: string }
>(
  "dataQuality/createReconciliationCheck",
  async (
    { domainId, metricKey, name, primarySql, independentSql, tolerance },
    { rejectWithValue },
  ) => {
    try {
      const res = await apiFetch(`/v1/domains/${domainId}/semantic-model/reconciliation-checks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          metric_key: metricKey,
          name,
          primary_sql: primarySql,
          independent_sql: independentSql,
          tolerance,
        }),
      });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      const data: unknown = await res.json();
      if (!isReconciliationCheck(data)) {
        return rejectWithValue("Unexpected response shape");
      }
      return data;
    } catch (e) {
      const message = e instanceof Error ? e.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

export type RunReconciliationCheckArgs = {
  domainId: number;
  checkId: string;
};

export const runDomainReconciliationCheck = createAsyncThunk<
  ReconciliationCheck,
  RunReconciliationCheckArgs,
  { rejectValue: string }
>("dataQuality/runReconciliationCheck", async ({ domainId, checkId }, { rejectWithValue }) => {
  try {
    const res = await apiFetch(
      `/v1/domains/${domainId}/semantic-model/reconciliation-checks/${checkId}/run`,
      { method: "POST" },
    );
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isReconciliationCheck(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (e) {
    const message = e instanceof Error ? e.message : "Request failed";
    return rejectWithValue(message);
  }
});

const dataQualitySlice = createSlice({
  name: "dataQuality",
  initialState: dataQualityInitialState,
  reducers: {
    clearDataQuality(state) {
      state.domainId = null;
      state.integrityReport = null;
      state.integrityCheckStatus = STATUS.IDLE;
      state.integrityCheckError = null;
      state.reconciliationChecks = [];
      state.reconciliationChecksStatus = STATUS.IDLE;
      state.reconciliationChecksError = null;
      state.createReconciliationCheckStatus = STATUS.IDLE;
      state.createReconciliationCheckError = null;
      state.runningReconciliationCheckId = null;
      state.runReconciliationCheckError = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchDomainIntegrityCheck.pending, (state, action) => {
        state.integrityCheckStatus = STATUS.LOADING;
        state.integrityCheckError = null;
        if (state.domainId !== action.meta.arg) {
          state.domainId = action.meta.arg;
          state.integrityReport = null;
        }
      })
      .addCase(fetchDomainIntegrityCheck.fulfilled, (state, action) => {
        if (state.domainId !== action.payload.domainId) {
          return;
        }
        state.integrityCheckStatus = STATUS.SUCCEEDED;
        state.integrityReport = action.payload.report;
      })
      .addCase(fetchDomainIntegrityCheck.rejected, (state, action) => {
        state.integrityCheckStatus = STATUS.FAILED;
        state.integrityCheckError = action.payload ?? "Request failed";
      })
      .addCase(fetchDomainReconciliationChecks.pending, (state, action) => {
        state.reconciliationChecksStatus = STATUS.LOADING;
        state.reconciliationChecksError = null;
        if (state.domainId !== action.meta.arg) {
          state.domainId = action.meta.arg;
        }
      })
      .addCase(fetchDomainReconciliationChecks.fulfilled, (state, action) => {
        if (state.domainId !== action.payload.domainId) {
          return;
        }
        state.reconciliationChecksStatus = STATUS.SUCCEEDED;
        state.reconciliationChecks = action.payload.checks;
      })
      .addCase(fetchDomainReconciliationChecks.rejected, (state, action) => {
        state.reconciliationChecksStatus = STATUS.FAILED;
        state.reconciliationChecksError = action.payload ?? "Request failed";
      })
      .addCase(createDomainReconciliationCheck.pending, (state) => {
        state.createReconciliationCheckStatus = STATUS.LOADING;
        state.createReconciliationCheckError = null;
      })
      .addCase(createDomainReconciliationCheck.fulfilled, (state, action) => {
        state.createReconciliationCheckStatus = STATUS.SUCCEEDED;
        state.reconciliationChecks = [...state.reconciliationChecks, action.payload];
      })
      .addCase(createDomainReconciliationCheck.rejected, (state, action) => {
        state.createReconciliationCheckStatus = STATUS.FAILED;
        state.createReconciliationCheckError = action.payload ?? "Request failed";
      })
      .addCase(runDomainReconciliationCheck.pending, (state, action) => {
        state.runningReconciliationCheckId = action.meta.arg.checkId;
        state.runReconciliationCheckError = null;
      })
      .addCase(runDomainReconciliationCheck.fulfilled, (state, action) => {
        state.runningReconciliationCheckId = null;
        state.reconciliationChecks = state.reconciliationChecks.map((check) =>
          check.id === action.payload.id ? action.payload : check,
        );
      })
      .addCase(runDomainReconciliationCheck.rejected, (state, action) => {
        state.runningReconciliationCheckId = null;
        state.runReconciliationCheckError = action.payload ?? "Request failed";
      });
  },
});

export const { clearDataQuality } = dataQualitySlice.actions;
export const dataQualityReducer = dataQualitySlice.reducer;
